import { memo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonComponent, ModalComponent } from "@zenith/components";
import ArrowDown01 from "@zenith/assets/icons/ArrowDown01";
import PlusIcon from "@zenith/assets/icons/PlusIcon";
import GearCheckIcon from "@zenith/assets/icons/GearCheckIcon";
import Close from "@zenith/assets/icons/Close";
import OutletsLimitExceeded from "@zenith/assets/illustrations/OutletsLimitExceeded";
import { useOutletContext } from "@zenith/context";
import { usePermissions } from "@zenith/features/AuthZ/usePermissions";
import OutletStatus from "@zenith/features/OutletManagement/components/OutletStatusComponent/outlet-status-component";
import { useOutletView } from "@zenith/features/OutletManagement/hooks/useOutletView";
import { useGoHomeAfterSwitch } from "@zenith/features/OutletManagement/hooks/useGoHomeAfterSwitch";
import { useLoadOutlets } from "@zenith/features/OutletManagement/hooks/useLoadOutlets";
import { useSwitchOutlet } from "@zenith/features/OutletManagement/hooks/useSwitchOutlet";
import ContactSupportModal from "@zenith/pages/SendMoneyPage/components/ContactSupportModal";
import { OUTLET_CREATE } from "@zenith/routes/paths";
import { OutletListDTO, WalletApiService } from "@zenith/services/user";
import { formatCurrency } from "@zenith/utils";

const MAX_OUTLETS = 5;

// Values taken from the "Outlet Management: Outlet Switch Management" design.
const CARD_SELECTED =
  "relative flex h-[112.55px] w-full shrink-0 flex-col justify-center overflow-clip rounded-[15.705px] border-[1.047px] border-primary-300 bg-white px-[15.705px] text-left shadow-[0_0_0_1.309px_#FFFFFF,0_0_0_2.617px_#DCDCDE]";
const CARD_UNSELECTED =
  "relative flex h-[77.215px] w-full shrink-0 flex-col justify-center overflow-clip rounded-[15.705px] bg-[#F7F7F7] px-[15.705px] text-left";
const CARD_NAME = "text-[16px] font-medium leading-6 text-[#141B34]";
const CARD_META =
  "flex items-center gap-[10.47px] text-[12px] italic leading-[15.292px] text-[rgba(98,98,98,0.69)]";
/**
 * The design keeps a row's balance at the far left of that same line, but as a
 * balance rather than as meta — the styling above is italic and faded, which
 * made the money read like a caption.
 */
const CARD_META_BALANCE = "not-italic font-medium text-[#141B34]";
const CARD_BALANCE =
  "flex items-center gap-[4.773px] text-[16px] font-semibold leading-6 text-black";
const BADGE_TAB =
  "absolute top-0 right-0 flex h-[27.483px] w-[30.101px] items-center justify-center rounded-bl-[13.087px] bg-primary-300";

function StatusValue({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-[4px]">
      <span>Status:</span>
      <OutletStatus label={label} />
    </span>
  );
}

export interface OutletSwitcherComponentProps {
  onOutletsLoaded?: (hasOutlets: boolean) => void;
  /** Shown as a stand-in "default outlet" when the merchant has no real outlets yet */
  defaultOutletName?: string;
}

const OutletSwitcherComponent = memo(function OutletSwitcherComponent({
  onOutletsLoaded,
  defaultOutletName,
}: OutletSwitcherComponentProps) {
  const navigate = useNavigate();
  const { outletContext, setOutletContext, setOutletSelection } =
    useOutletContext();
  const { outlets, isLoadingOutlets } = outletContext;
  const { isAllOutletsSelected, selectedOutlet } = useOutletView();
  const { resolved, can } = usePermissions();
  const showsBalance = resolved && can("balance:view");

  // The All Outlets figure is every outlet's balance added up. The business
  // wallet is a separate figure and the two diverge once an outlet holds funds
  // of its own, so the wallet cannot stand in for the total.
  const allOutletsBalance = outlets.reduce(
    (total, outlet) => total + (outlet.availableBalance ?? outlet.balance ?? 0),
    0
  );

  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const [showExceededModal, setShowExceededModal] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number>();

  const loadOutletsFromApi = useLoadOutlets();
  const switchOutlet = useSwitchOutlet();
  const goHomeAfterSwitch = useGoHomeAfterSwitch();

  const loadOutlets = useCallback(() => {
    // The permitted outlets are the ones this user may act on, so an employee
    // sees their own rather than the owner's, each with its balance. A single
    // outlet already in view is signed into, so the server acts on it too.
    loadOutletsFromApi()
      .then((content) => onOutletsLoaded?.(content.length > 0))
      .catch((error) => {
        console.error("Failed to fetch outlets:", error);
        setOutletContext({ isLoadingOutlets: false });
        onOutletsLoaded?.(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadOutletsFromApi]);

  /**
   * Re-read every time the switcher opens. The navbar keeps this component
   * mounted across every route, so without this a freshly created outlet — or
   * a rename, or a new balance — never showed up until logging out and back in.
   */
  useEffect(() => {
    if (isSwitcherOpen) loadOutlets();
  }, [isSwitcherOpen, loadOutlets]);

  // Outlet balances come back with the outlet list itself, so only the business
  // wallet balance has to be fetched separately — and it is re-read on every
  // open so the switcher never shows stale figures.
  useEffect(() => {
    if (!isSwitcherOpen || !showsBalance) return;

    WalletApiService.getWalletDetails()
      .then((response) =>
        setWalletBalance(response.data?.balance?.availableBalance)
      )
      .catch((error) =>
        console.error("Failed to fetch wallet balance:", error)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSwitcherOpen, showsBalance]);

  const hasOutlets = outlets.length > 0;
  const switcherLabel = !hasOutlets
    ? defaultOutletName
    : isAllOutletsSelected
    ? "All Outlets"
    : selectedOutlet?.name || "Outlet";

  const handleAddNew = () => {
    setIsSwitcherOpen(false);
    if (outlets.length >= MAX_OUTLETS) {
      setShowExceededModal(true);
      return;
    }
    navigate(OUTLET_CREATE);
  };

  if (isLoadingOutlets || (!hasOutlets && !defaultOutletName)) {
    return null;
  }

  return (
    <>
      <button
        id="outlet-switcher-button"
        type="button"
        onClick={() => setIsSwitcherOpen(true)}
        className="mx-3 hidden h-[46px] w-fit max-w-[240px] flex-row items-center gap-[4px] rounded-[43px] bg-[#F0F0F0] px-[20px] md:flex"
      >
        <span
          className="truncate text-[12px] font-normal leading-[18px] text-[#3D2E2D]"
          title={switcherLabel}
        >
          Outlet: {switcherLabel}
        </span>
        <ArrowDown01
          className={`shrink-0 text-black transition-transform ${
            isSwitcherOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <ModalComponent
        showModal={isSwitcherOpen}
        onClickClose={() => setIsSwitcherOpen(false)}
        showCloseIcon={false}
        hideButtons
        classNames="!w-[438px] !max-w-[438px] !rounded-[20px] !p-6 !gap-0 max-h-[90vh]"
        bodyClassName="w-full"
        title=""
        iconNode={
          <div className="mb-4 flex w-full items-start justify-between">
            <div className="h-6 w-6" />
            <button
              id="outlet-switcher-close-button"
              type="button"
              aria-label="Close"
              className="cursor-pointer"
              onClick={() => setIsSwitcherOpen(false)}
            >
              <Close width={24} height={24} />
            </button>
          </div>
        }
        body={
          <div className="flex max-h-[60vh] w-full flex-col overflow-y-auto">
            <p className="text-center text-[20px] font-medium leading-7 text-[#1A1C1F]">
              Outlets
            </p>
            <div className="mt-6 flex w-full flex-col gap-6">
              {!hasOutlets && defaultOutletName && (
                <div className={CARD_SELECTED}>
                  <span className={BADGE_TAB}>
                    <GearCheckIcon className="h-[15.705px] w-[15.705px] text-white" />
                  </span>
                  <span className={CARD_NAME}>{defaultOutletName}</span>
                  {showsBalance && (
                    <p className={`${CARD_BALANCE} mt-[4px]`}>
                      {formatCurrency(walletBalance)}
                    </p>
                  )}
                </div>
              )}

              {outlets.length > 1 && (
                <button
                  type="button"
                  id="outlet-switcher-all-outlets"
                  onClick={() => {
                    setIsSwitcherOpen(false);
                    if (isAllOutletsSelected) return;
                    setOutletSelection({ kind: "all" });
                    goHomeAfterSwitch();
                  }}
                  className={
                    isAllOutletsSelected ? CARD_SELECTED : CARD_UNSELECTED
                  }
                >
                  {isAllOutletsSelected && (
                    <span className={BADGE_TAB}>
                      <GearCheckIcon className="h-[15.705px] w-[15.705px] text-white" />
                    </span>
                  )}
                  <span className={CARD_NAME}>All Outlets</span>
                  {/* All Outlets is not a single outlet - it has no ID and
                      no active/inactive state of its own, just the combined
                      balance. */}
                  {showsBalance && (
                    <p className={`${CARD_BALANCE} mt-[4px]`}>
                      {formatCurrency(allOutletsBalance)}
                    </p>
                  )}
                </button>
              )}

              {outlets.map((outlet) => {
                const isSelected =
                  Boolean(outlet.id) && selectedOutlet?.id === outlet.id;
                const statusLabel =
                  outlet.status === OutletListDTO.status.ACTIVE
                    ? "Active"
                    : "Inactive";
                const balance = outlet.availableBalance ?? outlet.balance;

                return (
                  <button
                    type="button"
                    key={outlet.id}
                    id={`outlet-switcher-option-${outlet.id}`}
                    onClick={async () => {
                      if (isSelected) {
                        setIsSwitcherOpen(false);
                        return;
                      }
                      try {
                        await switchOutlet(outlet);
                      } catch (error) {
                        console.error("Failed to switch outlet:", error);
                        return;
                      }
                      if (outlet.id) {
                        setOutletSelection({
                          kind: "outlets",
                          ids: [outlet.id],
                        });
                      }
                      setIsSwitcherOpen(false);
                      goHomeAfterSwitch();
                    }}
                    className={isSelected ? CARD_SELECTED : CARD_UNSELECTED}
                  >
                    {isSelected && (
                      <span className={BADGE_TAB}>
                        <GearCheckIcon className="h-[15.705px] w-[15.705px] text-white" />
                      </span>
                    )}
                    <span className={CARD_NAME}>{outlet.name}</span>
                    {isSelected ? (
                      <span className="flex w-full items-end justify-between gap-3">
                        <span className="flex min-w-0 flex-col">
                          <span className={`${CARD_META} mt-[4px]`}>
                            <span>Outlet ID: {outlet.id}</span>
                            <span>-</span>
                            <StatusValue label={statusLabel} />
                          </span>
                        </span>
                        {showsBalance && (
                          <span className={`${CARD_BALANCE} shrink-0`}>
                            {formatCurrency(balance)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <p className={`${CARD_META} mt-[4px]`}>
                        {showsBalance && (
                          <>
                            <span className={CARD_META_BALANCE}>
                              {formatCurrency(balance)}
                            </span>
                            <span>-</span>
                          </>
                        )}
                        <StatusValue label={statusLabel} />
                      </p>
                    )}
                  </button>
                );
              })}

              <ButtonComponent
                id="outlet-switcher-add-new"
                label="Add New"
                size="large"
                type="secondary"
                fullWidth
                className="mt-2"
                iconStart={<PlusIcon stroke="currentColor" />}
                onClick={handleAddNew}
              />
            </div>
          </div>
        }
      />

      <ModalComponent
        showModal={showExceededModal}
        onClickClose={() => setShowExceededModal(false)}
        showCloseIcon={false}
        hideButtons
        classNames="!w-[438px] !max-w-[438px] !rounded-[20px] !px-6 !pt-6 !pb-12"
        bodyClassName="w-full"
        title=""
        iconNode={
          <>
            <div className="flex w-full items-start justify-between">
              <div className="h-6 w-6" />
              <button
                id="outlet-exceeded-close-button"
                type="button"
                aria-label="Close"
                className="cursor-pointer"
                onClick={() => setShowExceededModal(false)}
              >
                <Close width={24} height={24} />
              </button>
            </div>
            <OutletsLimitExceeded className="mt-4 mb-4 h-[92.439px] w-[168px]" />
          </>
        }
        body={
          <div className="flex w-full flex-col items-center">
            <p className="text-center text-[20px] font-semibold leading-7 text-[#1A1C1F]">
              Outlet Number Exceeded!
            </p>
            <p className="mt-4 w-[252px] text-center text-[12px] leading-4 text-[#71716F]">
              You have exceeded the number of outlets ({MAX_OUTLETS}) that you
              can add. Kindly contact our support team to create more outlets.
            </p>
            <div className="mt-12 flex w-full gap-5">
              <ButtonComponent
                id="outlet-exceeded-go-to-outlet-button"
                label="Go To Outlet"
                type="secondary"
                size="large"
                fullWidth
                className="!h-12 !text-base !font-normal"
                onClick={() => setShowExceededModal(false)}
              />
              <ButtonComponent
                id="outlet-exceeded-contact-support-button"
                label="Contact Support"
                type="primary"
                size="large"
                fullWidth
                className="!h-12 !text-base !font-normal"
                onClick={() => {
                  setShowExceededModal(false);
                  setShowContactSupport(true);
                }}
              />
            </div>
          </div>
        }
      />

      <ContactSupportModal
        open={showContactSupport}
        onClose={() => setShowContactSupport(false)}
      />
    </>
  );
});

export default OutletSwitcherComponent;
